import { useEffect, useState } from 'react';

/**
 * A two-route switch, which is all this site needs.
 *
 * React Router would add a dependency and a provider tree to choose between
 * exactly two pages. The hash form is accepted alongside the path form so the
 * page still works if it is ever served somewhere without a single-page
 * rewrite rule configured.
 */

function currentRoute() {
  if (typeof window === 'undefined') return '/';

  const hash = window.location.hash.replace(/^#/, '');
  if (hash.startsWith('/')) return normalise(hash);

  return normalise(window.location.pathname);
}

function normalise(path) {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed.toLowerCase();
}

export function useRoute() {
  const [route, setRoute] = useState(currentRoute);

  useEffect(() => {
    const update = () => setRoute(currentRoute());
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('hashchange', update);
    };
  }, []);

  return route;
}

/** Navigate without a full page load, keeping the back button working. */
export function navigate(path) {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
  // A real page load starts at the top; pushState keeps the old scroll
  // position, so clicking a link half way down the homepage would open the
  // next page already scrolled. Only forward navigation is reset -- the back
  // button still restores where the reader was.
  window.scrollTo(0, 0);
}

/** Props for an anchor that navigates in-page but still behaves like a link. */
export function linkProps(path) {
  return {
    href: path,
    onClick: (event) => {
      // Leave modified clicks alone so "open in new tab" still works.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      navigate(path);
    },
  };
}
